import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { organisationServices } from '../../service/companySettingService';
import { TOrganisationUploadKey } from '../../types/companySettings';

type DropzoneProps = {
  accept?: Accept;
  multiple?: boolean;
  value?: string; // file path or url for preview
  uploadKey: TOrganisationUploadKey;
  onChange: (path: string) => void;
};

export const ImageDropzone: React.FC<DropzoneProps> = ({
  accept,
  multiple = false,
  value,
  uploadKey,
  onChange,
}) => {
  const [preview, setPreview] = useState<string | undefined>(value);

  const uploadMutation = organisationServices.useUploadOrganisationFile();
  const isUploading = uploadMutation.isPending;

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } =
    useDropzone({
      onDrop: files => {
        if (files?.[0]) {
          uploadMutation.mutate(
            { file: files[0], fileName: uploadKey },
            {
              onSuccess: data => {
                const filePath = data?.data?.filePath || data?.data?.url || '';
                onChange(filePath);
                toast.success('File uploaded successfully');
              },
              onError: (error: Error) => {
                toast.error(`Failed to upload file: ${error.message}`);
                setPreview(undefined);
              },
            }
          );
        }
      },
      accept,
      multiple,
      disabled: isUploading,
    });

  // Update preview when a new file is selected
  useEffect(() => {
    if (acceptedFiles && acceptedFiles[0]) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = e => setPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  }, [acceptedFiles]);

  // Reset preview when value is cleared
  // In ImageDropzone.tsx - update this useEffect
  useEffect(() => {
    if (value) {
      // Check if it's already a data URL or a regular URL
      if (value.startsWith('data:') || value.startsWith('http')) {
        setPreview(value);
      } else if (value.startsWith('/9j/')) {
        // Base64 JPEG (starts with /9j/)
        setPreview(`data:image/jpeg;base64,${value}`);
      } else if (value.startsWith('iVBOR')) {
        // Base64 PNG (starts with iVBOR)
        setPreview(`data:image/png;base64,${value}`);
      } else {
        // Assume it's a base64 image, try JPEG
        setPreview(`data:image/jpeg;base64,${value}`);
      }
    } else {
      setPreview(undefined);
    }
  }, [value]);

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreview(undefined);
    onChange('');
  };

  return (
    <div
      {...getRootProps()}
      className={`relative flex min-h-[140px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-white p-4 transition-colors duration-200 ${
        isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
      } ${isUploading ? 'pointer-events-none opacity-70' : ''} hover:border-blue-400 hover:bg-blue-50`}
    >
      <input {...getInputProps()} />

      {/* Loading bar */}
      {isUploading && (
        <div className="absolute bottom-0 left-0 h-1 w-full overflow-hidden rounded-b-lg bg-gray-200">
          <div className="h-full w-full animate-pulse bg-blue-500" />
        </div>
      )}

      {preview ? (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="h-24 w-auto rounded object-contain"
          />
          {/* Uploading overlay */}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center rounded bg-white/80">
              <div className="text-center">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent mx-auto" />
                <div className="text-xs text-gray-500 mt-1">Uploading...</div>
              </div>
            </div>
          )}
          {/* Remove button */}
          {!isUploading && (
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 z-20 rounded-full border border-gray-300 bg-white p-1 shadow transition-colors hover:bg-red-500 hover:text-white"
              tabIndex={-1}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ) : (
        <div className="text-center">
          <div className="mb-2 text-gray-400">
            <svg
              className="mx-auto h-10 w-10"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="text-sm text-gray-600">
            {isDragActive
              ? 'Drop the file here...'
              : 'Drag & drop or click to upload'}
          </p>
          <p className="mt-1 text-xs text-gray-400">PNG, JPG up to 5MB</p>
        </div>
      )}
    </div>
  );
};

export default ImageDropzone;
